// src/utils/datadog.ts
import { datadogLogs } from '@datadog/browser-logs';
import type { LogsEvent } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import type { RumEvent } from '@datadog/browser-rum';
import { createBaseURL } from './axios';

const currentEnv = import.meta.env.VITE_ENV as keyof typeof createBaseURL;
const apiBaseUrl = createBaseURL[currentEnv];

/**
 * 앱 코드에서 발생한 에러가 아닌 것들을 Datadog 전송 전에 제외합니다.
 * (브라우저 확장 프로그램 / 검색엔진 봇 / 브라우저 intervention 경고)
 * 전송만 막을 뿐 앱 동작에는 영향을 주지 않습니다.
 */
const BOT_USER_AGENT_PATTERN =
  /bot|crawler|spider|crawling|googlebot|bingbot|yeti|slurp|duckduckbot|baiduspider|facebookexternalhit|headlesschrome/i;

const NOISE_ERROR_PATTERNS = [
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-(web-)?extension:\/\//i,
  /^intervention:/i,
  /Ignored attempt to cancel a touchmove event/i,
  /ResizeObserver loop/i,
];

const isNoiseErrorText = (text: string) => NOISE_ERROR_PATTERNS.some((pattern) => pattern.test(text));

const isBotUserAgent = () =>
  typeof navigator !== 'undefined' && BOT_USER_AGENT_PATTERN.test(navigator.userAgent ?? '');

const isNoiseRumEvent = (event: RumEvent) => {
  if (event.type !== 'error') return false;
  if (isBotUserAgent()) return true;

  const { message = '', stack = '', type = '' } = event.error ?? {};
  return isNoiseErrorText(`${type}\n${message}\n${stack}`);
};

const isNoiseLogsEvent = (event: LogsEvent) => {
  if (event.status !== 'error') return false;
  if (isBotUserAgent()) return true;

  const { message = '', stack = '', kind = '' } = event.error ?? {};
  return isNoiseErrorText(`${kind}\n${message}\n${stack}\n${event.message ?? ''}`);
};

export const initializeDatadog = () => {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'dpost_portal',
    env: import.meta.env.VITE_DATADOG_ENV || 'prod',
    version: __APP_VERSION__ || '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [apiBaseUrl],
    useSecureSessionCookie: true,
    sessionPersistence: ['cookie', 'local-storage'],
    trackAnonymousUser: false, // 쿠키 15분만 유지
    beforeSend: (event) => !isNoiseRumEvent(event),
  });

  datadogRum.startSessionReplayRecording();

  datadogLogs.init({
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: 'dpost_portal',
    env: import.meta.env.VITE_DATADOG_ENV || 'prod',
    version: __APP_VERSION__ || '1.0.0',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'],
    sessionSampleRate: 100,
    sessionPersistence: ['cookie', 'local-storage'],
    beforeSend: (event) => !isNoiseLogsEvent(event),
  });
};
